import { compose, isFunction } from 'underscore';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useAttentionBeaconContext } from './use-attention-beacon-context';
import { getBeaconId } from './beacon-id';

export const withAttentionBeacon = (WrappedComponent, { Adapters }) => {
  const AttentionBeaconAdapter = compose(...Adapters)(WrappedComponent);

  const HOC = ({ attentionBeaconProps: attentionBeaconPropsFromProps, ...restProps }) => {
    const [beaconId, setBeaconId] = useState(-1);
    const needsAttentionRef = useRef(false);
    const [needsAttentionState, setNeedsAttentionState] = useState(needsAttentionRef.current);

    const {
      isIsolated, registerBeacon, unregisterBeacon, notifyStateChange, debugMode,
    } = useAttentionBeaconContext();

    const { onNeedsAttentionChange: onNeedsAttentionChangeFromProps } = attentionBeaconPropsFromProps;
    const onAdapterNeedsAttentionChange = useCallback((newNeedsAttention) => {
      needsAttentionRef.current = newNeedsAttention;
      setNeedsAttentionState(needsAttentionRef.current);
      if (isFunction(onNeedsAttentionChangeFromProps)) {
        onNeedsAttentionChangeFromProps(newNeedsAttention, beaconId);
      }
      if (!isIsolated) {
        notifyStateChange();
      }
    }, [isIsolated, onNeedsAttentionChangeFromProps, notifyStateChange, beaconId]);

    const needsAttentionQuery = useCallback(() => needsAttentionRef.current, []);

    useEffect(() => {
      const id = getBeaconId();
      setBeaconId(id);
      const beacon = {
        id, needsAttention: needsAttentionQuery,
      };
      if (!isIsolated) {
        registerBeacon(beacon);
      }
      return () => {
        if (!isIsolated) {
          unregisterBeacon(beacon);
        }
      };
    }, [isIsolated, needsAttentionQuery, registerBeacon, unregisterBeacon]);

    const attentionBeaconAdapterProps = useMemo(() => ({
      onAdapterNeedsAttentionChange,
      debugMode,
    }), [onAdapterNeedsAttentionChange, debugMode]);

    return (
      <>
        {debugMode &&
          <div>{`Beacon ${beaconId}`}<br /> {(needsAttentionState ? '!!!' : 'Ok')}</div>
        }
        <AttentionBeaconAdapter {...restProps} attentionBeaconAdapterProps={attentionBeaconAdapterProps} />
      </>
    );
  };

  HOC.propTypes = {
    attentionBeaconProps: PropTypes.shape({
      onNeedsAttentionChange: PropTypes.func,
    }),
  };

  HOC.defaultProps = {
    attentionBeaconProps: {
      onNeedsAttentionChange: undefined,
    },
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withAttentionBeacon(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
